import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { api } from '../../api';

const emptySetForm = { name: '', note: '' };
const emptyQuestionForm = { type: 'ABCD', text: '', options: { A: '', B: '', C: '', D: '' }, correctOption: 'A', correctBool: true };

export function QuestionSetsPage() {
  const [sets, setSets] = useState([]);
  const [selectedSet, setSelectedSet] = useState(null);
  const [setForm, setSetForm] = useState(emptySetForm);
  const [questionForm, setQuestionForm] = useState(emptyQuestionForm);
  const [editingQuestionId, setEditingQuestionId] = useState(null);
  const [editingSetId, setEditingSetId] = useState(null);
  const [setDialogOpen, setSetDialogOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadSets = async () => setSets(await api.getQuestionSets());
  const loadSetDetail = async (id) => setSelectedSet(await api.getQuestionSet(id));

  useEffect(() => {
    loadSets();
  }, []);

  const openCreateSet = () => {
    setSetDialogOpen(true);
    setEditingSetId(null);
    setSetForm(emptySetForm);
  };

  const openEditSet = (setItem) => {
    setSetDialogOpen(true);
    setEditingSetId(setItem.id);
    setSetForm({ name: setItem.name, note: setItem.note || '' });
  };

  const saveSet = async () => {
    try {
      if (editingSetId) {
        await api.updateQuestionSet(editingSetId, setForm);
      } else {
        await api.createQuestionSet(setForm);
      }
      setSetDialogOpen(false);
      setMessage('Set pitanja je spremljen.');
      setError('');
      await loadSets();
      if (selectedSet?.id === editingSetId) await loadSetDetail(editingSetId);
    } catch (err) {
      setError(err.message);
      setMessage('');
    }
  };

  const saveQuestion = async () => {
    try {
      if (!selectedSet) return;
      if (editingQuestionId) {
        await api.updateQuestion(selectedSet.id, editingQuestionId, questionForm);
      } else {
        await api.createQuestion(selectedSet.id, questionForm);
      }
      setQuestionForm(emptyQuestionForm);
      setEditingQuestionId(null);
      setMessage('Pitanje je spremljeno.');
      setError('');
      await loadSets();
      await loadSetDetail(selectedSet.id);
    } catch (err) {
      setError(err.message);
      setMessage('');
    }
  };

  return (
    <Container maxWidth="xl">
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Pitanja</Typography>
          <Button variant="contained" onClick={openCreateSet}>Dodaj novi set pitanja</Button>
        </Stack>
        {message && <Alert severity="success">{message}</Alert>}
        {error && <Alert severity="error">{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Stack spacing={2}>
                  <Typography variant="h6">Setovi pitanja</Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Naziv</TableCell>
                        <TableCell>Broj pitanja</TableCell>
                        <TableCell>Akcije</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sets.map((setItem) => (
                        <TableRow key={setItem.id} hover selected={selectedSet?.id === setItem.id}>
                          <TableCell>{setItem.name}</TableCell>
                          <TableCell>{setItem.questionCount}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={1}>
                              <Button size="small" onClick={() => loadSetDetail(setItem.id)}>Uredi</Button>
                              <Button size="small" onClick={() => openEditSet(setItem)}>Postavke</Button>
                              <Button size="small" color="error" onClick={async () => {
                                try {
                                  await api.deleteQuestionSet(setItem.id);
                                  setMessage('Set pitanja je obrisan.');
                                  setError('');
                                  if (selectedSet?.id === setItem.id) setSelectedSet(null);
                                  await loadSets();
                                } catch (err) {
                                  setError(err.message);
                                  setMessage('');
                                }
                              }}>Obriši</Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                {!selectedSet ? (
                  <Typography>Odaberite set pitanja za uređivanje.</Typography>
                ) : (
                  <Stack spacing={2}>
                    <Typography variant="h6">{selectedSet.name}</Typography>
                    <Typography color="text.secondary">{selectedSet.note || 'Nema napomene.'}</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Pitanje</TableCell>
                          <TableCell>Vrsta</TableCell>
                          <TableCell>Akcije</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedSet.questions.map((question) => (
                          <TableRow key={question.id}>
                            <TableCell>{question.text}</TableCell>
                            <TableCell>{question.type === 'TF' ? 'Točno/Netočno' : 'ABCD'}</TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <Button size="small" onClick={() => {
                                  setEditingQuestionId(question.id);
                                  setQuestionForm({
                                    type: question.type,
                                    text: question.text,
                                    options: question.options || { A: '', B: '', C: '', D: '' },
                                    correctOption: question.correctOption || 'A',
                                    correctBool: question.correctBool ?? true
                                  });
                                }}>Uredi</Button>
                                <Button size="small" color="error" onClick={async () => {
                                  await api.deleteQuestion(selectedSet.id, question.id);
                                  setMessage('Pitanje je obrisano.');
                                  setError('');
                                  await loadSets();
                                  await loadSetDetail(selectedSet.id);
                                }}>Obriši</Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <Typography variant="subtitle1">{editingQuestionId ? 'Uredi pitanje' : 'Dodaj pitanje'}</Typography>
                    <Select value={questionForm.type} onChange={(event) => setQuestionForm({ ...questionForm, type: event.target.value })}>
                      <MenuItem value="ABCD">ABCD</MenuItem>
                      <MenuItem value="TF">Točno / Netočno</MenuItem>
                    </Select>
                    <TextField fullWidth label="Pitanje" value={questionForm.text} onChange={(event) => setQuestionForm({ ...questionForm, text: event.target.value })} />
                    {questionForm.type === 'ABCD' ? (
                      <>
                        {['A', 'B', 'C', 'D'].map((optionKey) => (
                          <TextField
                            key={optionKey}
                            fullWidth
                            label={`Odgovor ${optionKey}`}
                            value={questionForm.options[optionKey]}
                            onChange={(event) => setQuestionForm({
                              ...questionForm,
                              options: { ...questionForm.options, [optionKey]: event.target.value }
                            })}
                          />
                        ))}
                        <Select value={questionForm.correctOption} onChange={(event) => setQuestionForm({ ...questionForm, correctOption: event.target.value })}>
                          {['A', 'B', 'C', 'D'].map((optionKey) => (
                            <MenuItem key={optionKey} value={optionKey}>Točan odgovor: {optionKey}</MenuItem>
                          ))}
                        </Select>
                      </>
                    ) : (
                      <Select value={String(questionForm.correctBool)} onChange={(event) => setQuestionForm({ ...questionForm, correctBool: event.target.value === 'true' })}>
                        <MenuItem value="true">Tvrdnja je točna</MenuItem>
                        <MenuItem value="false">Tvrdnja je netočna</MenuItem>
                      </Select>
                    )}
                    <Stack direction="row" spacing={2}>
                      <Button variant="contained" onClick={saveQuestion}>Spremi pitanje</Button>
                      <Button onClick={() => { setQuestionForm(emptyQuestionForm); setEditingQuestionId(null); }}>Odustani</Button>
                    </Stack>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog open={setDialogOpen} onClose={() => setSetDialogOpen(false)} fullWidth maxWidth="sm">
          <DialogTitle>{editingSetId ? 'Uredi set pitanja' : 'Dodaj novi set pitanja'}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField fullWidth label="Naziv seta pitanja" value={setForm.name} onChange={(event) => setSetForm({ ...setForm, name: event.target.value })} />
              <TextField fullWidth multiline minRows={3} label="Napomena" value={setForm.note} onChange={(event) => setSetForm({ ...setForm, note: event.target.value })} />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSetDialogOpen(false)}>Odustani</Button>
            <Button variant="contained" onClick={saveSet}>Spremi</Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Container>
  );
}
